import * as z from 'zod'

import type { ListOrdersParams } from '@/api/gen'
import { COMMISSION_STATUSES } from '@/utils/commission-status'

/**
 * URL search-param schema + serialization helpers for /admin/orders.
 *
 * Same contract as /admin/partners: the URL is the single source of truth for
 * filters and pagination, so report-row and dashboard drill-downs are plain
 * navigations (`/admin/orders?from=…&to=…`), shareable and refresh-proof.
 */

const optionalDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
  .optional()

export const ordersSearchSchema = z.object({
  // Partner scope (UUID) — set when arriving from partner-centric surfaces.
  partner: z.uuid().optional(),
  // Sealed enum so typos and stale values never reach the BE call.
  status: z.enum(COMMISSION_STATUSES).optional(),
  q: z.string().optional(),
  from: optionalDateString,
  to: optionalDateString,
  page: z.coerce.number().int().positive().optional(),
})

export type OrdersSearch = z.infer<typeof ordersSearchSchema>

/**
 * The "everything at defaults" search — the route's `validateSearch` `.catch()`s
 * to this so a malformed link reverts to the canonical view instead of throwing
 * inside the router. All `undefined`, so the router serializer strips them from the URL.
 */
export const DEFAULT_ORDERS_SEARCH: OrdersSearch = {
  partner: undefined,
  status: undefined,
  q: undefined,
  from: undefined,
  to: undefined,
  page: undefined,
}

/**
 * Translate the URL state into the Orval `ListOrdersParams` wire shape.
 * Only keys that carry a value are set, keeping the generated query key
 * stable (Orval includes the params object in the key).
 */
export const toListOrdersParams = (search: OrdersSearch, pageSize: number): ListOrdersParams => {
  const params: ListOrdersParams = {
    page: search.page ?? 1,
    page_size: pageSize,
  }

  if (search.partner !== undefined) {
    params.partner_id = search.partner
  }
  if (search.status !== undefined) {
    params.status = search.status
  }
  if (search.q !== undefined && search.q !== '') {
    params.search = search.q
  }
  if (search.from !== undefined) {
    params.date_from = search.from
  }
  if (search.to !== undefined) {
    params.date_to = search.to
  }

  return params
}
